import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { LogicGroup, LogicGroupPayload } from '@/types/logic';
import ajax from '@/utils/ajax';

const basePath = '/api/dugout/logic_groups';

export const logicGroupRepository = {
  /**
   * Lists the root LogicGroups (with their nested child groups and elements
   * already recursively serialized) for one subject within one application --
   * i.e. the whole criteria tree in a single request.
   */
  async list({
    host,
    applicationId,
    subjectId
  }: {
    host: string;
    applicationId: string;
    subjectId: string;
  }): Promise<LogicGroup[]> {
    const response = await ajax.get({
      url: `${host}${basePath}/`,
      params: { logic_application: applicationId, subject_id: subjectId }
    });
    return response.data as LogicGroup[];
  },

  async create({ host, data }: { host: string; data: LogicGroupPayload }): Promise<LogicGroup> {
    const response = await ajax.post({ url: `${host}${basePath}/`, data });
    return response.data as unknown as LogicGroup;
  },

  async update({
    host,
    id,
    data
  }: {
    host: string;
    id: string;
    data: Partial<LogicGroupPayload>;
  }): Promise<LogicGroup> {
    // PATCH, not PUT -- callers only ever send the fields that changed (e.g. just
    // `logic_operator`), and a PUT would require every serializer field to be
    // re-sent, including ones like `subject_id` that are inherited/immutable here.
    const response = await ajax.patch({ url: `${host}${basePath}/${id}/`, data });
    return response.data as unknown as LogicGroup;
  },

  async delete({ host, id }: { host: string; id: string }): Promise<void> {
    await ajax.delete({ url: `${host}${basePath}/${id}/` });
  }
};

const LogicGroupRepository = () => {
  const { host } = useContext(AppContext);

  return {
    list: (applicationId: string, subjectId: string) =>
      logicGroupRepository.list({ host, applicationId, subjectId }),

    create: (data: LogicGroupPayload) => logicGroupRepository.create({ host, data }),

    update: (id: string, data: Partial<LogicGroupPayload>) =>
      logicGroupRepository.update({ host, id, data }),

    delete: (id: string) => logicGroupRepository.delete({ host, id })
  };
};

export default LogicGroupRepository;
